import { normalizeBackendChanges } from "../../../../../../utils/audit-log/normalize-backend-changes.ts"

interface ListDiff {
  added: string[]
  removed: string[]
}

interface ChangeRow {
  key: string
  field: string
  from: unknown
  to: unknown
  /** Set only for a file/attachment-like field whose from/to are item lists. */
  listDiff?: ListDiff
}

/** The real change keys seen for file-list fields (Project and Lead both use "attachment"/"attachments"). */
const LIST_LIKE_KEYS = new Set([
  "attachment",
  "attachments",
  "file",
  "files",
  // An invoice's linked ledger lines, audited as a list of "name: amount" items.
  "lines"
])

const parseListItems = (value: unknown): string[] => {
  if (Array.isArray(value))
    return value.map((v) => String(v).trim()).filter(Boolean)
  if (typeof value === "string")
    return value
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean)
  return []
}

/** `undefined` unless the key is a known file-list field with a real add/remove difference. */
const listDiffFor = (key: string, from: unknown, to: unknown): ListDiff | undefined => {
  if (!LIST_LIKE_KEYS.has(key.trim().toLowerCase())) return undefined

  const fromItems = parseListItems(from)
  const toItems = parseListItems(to)
  const fromSet = new Set(fromItems)
  const toSet = new Set(toItems)

  const added = toItems.filter((item) => !fromSet.has(item))
  const removed = fromItems.filter((item) => !toSet.has(item))

  return added.length || removed.length ? { added, removed } : undefined
}

/** Keys whose humanized name would not read as what they are. */
type FieldLabels = Partial<Record<string, string>>

const humanizeFieldKey = (key: string, labels?: FieldLabels): string =>
  labels?.[key] ??
  (key ? key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : key)

const isEmptyArrayLike = (value: unknown): boolean => {
  if (Array.isArray(value) && value.length === 0) return true
  if (typeof value === "string") {
    const t = value.trim()
    if (t === "" || t === "[]") return true
    try {
      const parsed = JSON.parse(t) as unknown
      return Array.isArray(parsed) && parsed.length === 0
    } catch {
      return false
    }
  }
  return false
}

const displayChangeValue = (value: unknown): string => {
  if (value === null || value === undefined || value === "" || isEmptyArrayLike(value))
    return "—"
  if (typeof value === "boolean") return value ? "Yes" : "No"
  if (typeof value === "object") {
    try {
      return JSON.stringify(value)
    } catch {
      return String(value)
    }
  }
  return String(value)
}

const isFromToFieldMap = (
  value: unknown
): value is Record<string, { from: unknown; to: unknown }> => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false
  const entries = Object.entries(value as Record<string, unknown>)
  if (!entries.length) return false
  return entries.every(([, v]) => {
    if (v === null || typeof v !== "object" || Array.isArray(v)) return false
    const o = v as Record<string, unknown>
    return "from" in o && "to" in o
  })
}

const isLegacyChangeArray = (
  value: unknown
): value is Array<{ field: string; old?: unknown; new?: unknown }> => {
  if (!Array.isArray(value) || value.length === 0) return false
  return value.every(
    (item) =>
      item &&
      typeof item === "object" &&
      !Array.isArray(item) &&
      typeof (item as { field?: unknown }).field === "string"
  )
}

/** `null` when `changes` has no recognizable field-level shape — callers fall back to raw display. */
const changeRowsFromChanges = (
  changes: unknown,
  labels?: FieldLabels
): ChangeRow[] | null => {
  const normalized = normalizeBackendChanges(changes)

  if (isFromToFieldMap(normalized)) {
    return Object.entries(normalized).map(([key, { from, to }]) => ({
      key,
      field: humanizeFieldKey(key, labels),
      from,
      to,
      listDiff: listDiffFor(key, from, to)
    }))
  }

  if (isLegacyChangeArray(normalized)) {
    return normalized.map((row, i) => ({
      key: `${row.field}-${i}`,
      field: humanizeFieldKey(row.field, labels),
      from: row.old,
      to: row.new,
      listDiff: listDiffFor(row.field, row.old, row.new)
    }))
  }

  return null
}

const hasChangeDetails = (changes: unknown): boolean => {
  if (changes === null || changes === undefined || changes === "") return false
  if (Array.isArray(changes)) return changes.length > 0
  if (typeof changes === "object") return Object.keys(changes as object).length > 0
  return true
}

/** "3 fields changed" reads as "3 fields created" on a Create row, "...deleted" on Delete. */
const changeVerbForAction = (action: string): "created" | "deleted" | "changed" => {
  const a = (action ?? "").trim().toLowerCase()
  if (a.startsWith("create")) return "created"
  if (a.startsWith("delete")) return "deleted"
  return "changed"
}

export {
  type ChangeRow,
  type ListDiff,
  changeRowsFromChanges,
  changeVerbForAction,
  displayChangeValue,
  hasChangeDetails,
  humanizeFieldKey
}
